import { NormalButton, CustomButton } from '@/shared/ui/components';
import { DeviceInformationTable } from '@/pages/new-rma/@x/rma-details';
import { forwardRef } from 'react';
import { t } from 'ttag';
import LabelInformation from '../labelInformation';
import { renderNotificationEmail } from './handler';
import { useTicketOutbound } from './useTicketOutbound';
import type { TicketOutboundProps } from './ticketOutbound.types';
import './ticketOutbound.scss';

/**
 * Outbound view of an RMA ticket, showing the devices sent to Biotricity and their shipment labels. Reads the breakdown from useTicketOutbound and renders DeviceInformationTable, notification email badges and clinic notes, the paired address table, and one LabelInformation per label refreshing through handleRefreshTracking tagged as Outbound; an Approved ticket with a clinic shipment reminder adds a banner and a create shipping label button.
 *
 * @returns Outbound RMA ticket content element.
 */
const TicketOutbound = forwardRef(
  ({
    hasRenewalStock = false,
    listStatesUS,
    listStatesCA,
    listCountries,
    data,
    handleRefreshTracking,
  }: TicketOutboundProps) => {
    const {
      filePdf,
      handleCreateShippingLabelClick,
      rmaDevicesArray,
      notificationEmail,
      clinicNotes,
      shippingInfo,
      labels,
    } = useTicketOutbound({ data });

    return (
      <div className="ticket-outbound">
        {data.status === 'Approved' && data?.clinicShipmentReminder?.isReminder && (
          <div className="draft-rma-label-notification">
            <div className="notification">
              {t`Create a shipment label and send your devices back to Biotricity for inspection and repair.`}
            </div>
            <NormalButton
              id="create-shipping-label-notification-button"
              className="create-shipping-label-notification-button"
              isText
              buttonName={t`Create shipping label`}
              onClick={handleCreateShippingLabelClick}
            />
          </div>
        )}
        <div className="rma-detail-content">
          <DeviceInformationTable
            rmaDevicesArray={rmaDevicesArray}
            hasRenewalStock={hasRenewalStock}
          />
          <div className="other-information">
            <div className="input-title">{t`Other information`}</div>
            <div className="other-information-content">
              <div className="other-information-row">
                <span className="title">{t`Notification email:`}</span>
                <span className="description">{renderNotificationEmail(notificationEmail)}</span>
              </div>
              <div className="other-information-row">
                <span className="title">{t`Notes:`}</span>
                <span className="description">{clinicNotes}</span>
              </div>
            </div>
          </div>

          <div className="shipping-information-table">
            <div className="input-title">{t`Shipping information`}</div>
            <div className="shipping-information-table-wrapper">
              <div className="table-title">
                <div className="shipping-title">{t`Shipping address`}</div>
                <div className="returning-title">{t`Returning address`}</div>
              </div>
              {shippingInfo?.map((x: any) => (
                <div className="table-row">
                  <div className="shipping-row">
                    <div className="title">{x.title}</div>
                    <div className="description">{x.shippingContent}</div>
                  </div>
                  <div className="returning-row">
                    <div className="title">{x.title}</div>
                    <div className="description">{x.returningContent}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div id="outboundTrackingInformation" className="tracking-information">
            <div className="input-title">
              <span>{t`Tracking information`}</span>
              {data.status === 'Approved' && (
                <CustomButton
                  size="small"
                  type="outline"
                  id="create-shipping-label-button"
                  onClick={handleCreateShippingLabelClick}
                >
                  {t`Create shipping label`}
                </CustomButton>
              )}
            </div>
            {labels?.length > 0 ? (
              labels?.map((item: any, index: any) => (
                <div className="tracking-information-item">
                  <LabelInformation
                    filePDF={filePdf}
                    index={index}
                    rate={item.rate}
                    stateUS={listStatesUS}
                    stateCA={listStatesCA}
                    country={listCountries}
                    shipmentDate={item.shipmentDate}
                    trackingHistory={item.trackingHistory}
                    transaction={item.transaction}
                    deviceLabel={item.shipmentDevices?.map((device: any) => device.deviceId)}
                    id={data.id}
                    rmaId={data.rmaId}
                    handleRefreshTracking={({ transactionId, ticketId, index, callback }) => {
                      handleRefreshTracking?.({
                        type: 'Outbound',
                        transactionId,
                        ticketId,
                        index,
                        callback,
                      });
                    }}
                  />
                </div>
              ))
            ) : (
              <div className="no-shipping-label">{t`No shipping label created yet`}</div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default TicketOutbound;
